import React, { useState, useRef, useEffect } from 'react';
import { Link, NavLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { UserCircleIcon, ArrowRightOnRectangleIcon, Squares2X2Icon, ArrowLeftOnRectangleIcon, WrenchScrewdriverIcon, DocumentChartBarIcon, ClipboardDocumentListIcon, DocumentTextIcon, ArchiveBoxIcon } from '@heroicons/react/24/outline';
import UserMenu from './UserMenu';
import { useAuth } from '../../contexts/AuthContext';
import { useModal } from '../../contexts/ModalContext';
import LanguageSelector from '../LanguageSelector';

const Header = () => {
  const { t } = useTranslation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const headerRef = useRef(null);
  const logoRef = useRef(null);
  const { openModal } = useModal();

  const { user, logout, isLoading: isLoadingAuth, isAuthenticated } = useAuth(); 

  useEffect(() => {
    const setHeaderHeight = () => {
      if (logoRef.current && headerRef.current) {
        const logoImg = logoRef.current;
        const onLogoLoad = () => {
          // Removed problematic height setting
        };
        if (logoImg.complete) {
          onLogoLoad();
        } else {
          logoImg.addEventListener('load', onLogoLoad);
        }
        return () => logoImg.removeEventListener('load', onLogoLoad);
      }
    };
    setHeaderHeight();
    window.addEventListener('resize', setHeaderHeight);
    return () => window.removeEventListener('resize', setHeaderHeight);
  }, []);

  const navLinks = [
    { title: t('nav.home'), path: '/' },
    { title: t('nav.services'), path: '/services' },
    { title: t('nav.about'), path: '/about' },
    { title: t('nav.testimonials'), path: '/testimonials' },
    { title: t('nav.blog'), path: '/blog' },
    { title: t('nav.contact'), path: '/contact' },
  ];

  const activeLinkClass = 'text-brand-accent-orange';
  const inactiveLinkClass = 'text-brand-text-dark hover:text-brand-accent-orange transition-colors duration-300';

  const closeMobileMenu = () => setIsMobileMenuOpen(false);

  // Admin/Manager menu items with unique keys
  const adminMenuItems = [
    { key: 'user-mgmt', to: '/admin/users', icon: Squares2X2Icon, label: t('admin.user_management') },
    { key: 'service-req', to: '/admin/service-requests', icon: WrenchScrewdriverIcon, label: t('admin.manage_requests') },
    { key: 'estimates', to: '/admin/estimates', icon: DocumentChartBarIcon, label: t('admin.estimates') },
    { key: 'quotes', to: '/admin/quotes', icon: ClipboardDocumentListIcon, label: t('admin.quotes') },
    { key: 'invoices', to: '/admin/invoices', icon: DocumentTextIcon, label: t('admin.invoices') },
    { key: 'expenses', to: '/admin/expenses', icon: DocumentChartBarIcon, label: t('admin.expenses') },
    { key: 'inventory', to: '/admin/inventory', icon: ArchiveBoxIcon, label: t('admin.inventory') },
    { key: 'tax-reporting', to: '/admin/tax-reporting', icon: DocumentChartBarIcon, label: t('admin.tax_reporting') },
  ];

  return (
    <header ref={headerRef} className="bg-brand-white shadow-md sticky top-[2.5rem] lg:top-[calc(2.5rem)] z-50 flex items-center min-h-[4.5rem] md:min-h-[5.5rem]">
      <div className="container mx-auto flex items-center justify-between container-padding">
        <Link to="/" className="flex items-center" aria-label={t('header.logo_alt')}>
          <img ref={logoRef} src="/images/qat-transparent.webp" alt="" width="400" height="159" className="h-12 md:h-16 w-auto self-center" />
        </Link>

        {/* Desktop Navigation & Auth */}
        <div className="hidden lg:flex items-center space-x-1">
          <nav className="flex space-x-1" aria-label="Primary">
            {navLinks.map((link) => (
              <NavLink
                key={`desktop-${link.title}`}
                to={link.path}
                className={({ isActive }) => 
                  `${isActive ? activeLinkClass : inactiveLinkClass} font-medium text-base py-2 px-3 rounded-md hover:bg-gray-50`
                }
              >
                {link.title}
              </NavLink>
            ))}
          </nav>

          {/* Desktop Auth Section & Language Selector */}
          <div className="ml-4 pl-4 border-l border-gray-200 flex items-center space-x-3">
            <LanguageSelector />
            {isLoadingAuth ? (
              <div className="h-8 w-20 bg-gray-200 animate-pulse rounded-md"></div>
            ) : isAuthenticated && user ? (
              <UserMenu userDisplayName={user.username || user.email} userRole={user.role} />
            ) : (
              <Link 
                to="/login"
                className={`${inactiveLinkClass} flex items-center font-medium text-sm py-2 px-3 rounded-md hover:bg-gray-100`}
              >
                <ArrowRightOnRectangleIcon className="h-5 w-5 mr-1.5" />
                {t('nav.login')}
              </Link>
            )}
          </div>
        </div>

        {/* Mobile Menu Button */}
        <div className="lg:hidden flex items-center space-x-2">
          <LanguageSelector className="scale-90" />
          <button
            type="button"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="text-brand-text-dark p-2 rounded-md hover:bg-gray-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-accent-orange"
            aria-label={t('header.menu_toggle')}
            aria-expanded={isMobileMenuOpen}
            aria-controls="mobile-nav"
          >
            <svg className="w-7 h-7" fill="none" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24" stroke="currentColor">
              {isMobileMenuOpen ? (
                <path d="M6 18L18 6M6 6l12 12" />
              ) : (
                <path d="M4 6h16M4 12h16M4 18h16" />
              )}
            </svg>
          </button>
        </div>
      </div>

      {/* Mobile Menu - Fixed conditional rendering */}
      {isMobileMenuOpen && (
        <div id="mobile-nav" className="lg:hidden bg-brand-white shadow-lg absolute top-full left-0 right-0 z-40 border-t border-gray-200">
          <nav className="flex flex-col py-4" aria-label="Mobile">
            {navLinks.map((link) => (
              <NavLink
                key={`mobile-${link.title}`}
                to={link.path}
                className={({ isActive }) => 
                  `${isActive ? activeLinkClass : inactiveLinkClass} font-medium text-base py-3 px-6 w-full text-left hover:bg-gray-50`
                }
                onClick={closeMobileMenu}
              >
                {link.title}
              </NavLink>
            ))}
            
            <div className="border-t border-gray-100 mt-2 pt-2 px-6">
              {isLoadingAuth ? (
                <div className="h-8 w-full bg-gray-200 animate-pulse rounded-md my-2"></div>
              ) : isAuthenticated && user ? (
                <React.Fragment>
                  {/* Render admin menu items only for admin/manager roles */}
                  {(user.role === 'admin' || user.role === 'manager') && 
                    adminMenuItems.map(({ key, to, icon: Icon, label }) => (
                      <Link
                      key={key}           /* 👈 stable key */
                        to={to}
                          className={`${inactiveLinkClass} flex items-center …`}
                          onClick={closeMobileMenu}
                          >
                        <Icon className="h-5 w-5 mr-2 text-gray-400" />
                        {label}
                      </Link>
                      ))
                    }
                  
                  <Link 
                    to="/profile-settings"
                    className={`${inactiveLinkClass} flex items-center font-medium text-base py-3 w-full text-left hover:bg-gray-50`}
                    onClick={closeMobileMenu}
                  >
                    <UserCircleIcon className="h-5 w-5 mr-2 text-gray-400"/>
                    {t('nav.profile')}
                  </Link>
                  
                  <button 
                    onClick={() => { logout(); closeMobileMenu(); }}
                    className={`${inactiveLinkClass} flex items-center font-medium text-base py-3 w-full text-left hover:bg-gray-50`}
                  >
                    <ArrowLeftOnRectangleIcon className="h-5 w-5 mr-2 text-gray-400"/>
                    {t('nav.logout')}
                  </button>
                </React.Fragment>
              ) : (
                <Link 
                  to="/login"
                  className={`${inactiveLinkClass} flex items-center font-medium text-base py-3 w-full text-left hover:bg-gray-50`}
                  onClick={closeMobileMenu}
                >
                  <ArrowRightOnRectangleIcon className="h-5 w-5 mr-2 text-gray-400"/>
                  {t('nav.login')}
                </Link>
              )}
            </div>
            
            <button 
              onClick={() => {
                openModal('scheduleService');
                closeMobileMenu();
              }}
              className="cta-button mx-6 mt-5 mb-3 text-center text-base py-3 w-auto block"
            >
              {t('header.schedule_service')}
            </button>
          </nav>
        </div>
      )}
    </header>
  );
};

export default Header;